import { Close } from '@mui/icons-material';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Slide,
  Snackbar,
  SnackbarProps,
  Stack,
  Typography,
} from '@mui/material';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type Options = Omit<SnackbarProps, 'action'> & {
  loading?: boolean;
  variant?: 'success' | 'error' | 'warning';
  action?: {
    label: string;
    href: string;
  };
};

type NotificationConfig = {
  id: string;
  message: string;
  options: Options;
};

type NotificationContextType = {
  // eslint-disable-next-line no-unused-vars
  showNotification: (message: string, options?: Options) => void;
};

const NotificationContext = createContext({} as NotificationContextType);

export default function NotificationProvider({
  children,
}: React.PropsWithChildren) {
  const [notifications, setNotifications] = useState<NotificationConfig[]>([]);

  const value = useMemo(
    () => ({
      showNotification: (message: string, options?: Options) => {
        setNotifications((prev) => [
          ...(true ? [] : prev),
          { id: window.crypto.randomUUID(), message, options: options || {} },
        ]);
      },
    }),
    [],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {notifications.map(({ id, message, options }) => {
        const { loading, variant, action, ...rest } = options;
        return (
          <NotificationItem
            key={id}
            id={id}
            message={message}
            loading={loading}
            variant={variant}
            action={action}
            rest={rest}
            onClose={() =>
              setNotifications((prev) => prev.filter((n) => n.id !== id))
            }
          />
        );
      })}
    </NotificationContext.Provider>
  );
}

function NotificationItem({
  message,
  loading,
  variant,
  action,
  rest,
  onClose,
}: {
  id: string;
  message: string;
  loading?: boolean;
  variant?: 'success' | 'error' | 'warning';
  action?: { label: string; href: string };
  rest: Omit<SnackbarProps, 'open' | 'onClose' | 'TransitionComponent'>;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  const handleActionClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If CTRL or CMD is held, let the browser handle it (opens in new tab)
    if (e.ctrlKey || e.metaKey) {
      return; // Let href handle it
    }
    // Otherwise, prevent default and navigate in same tab
    e.preventDefault();
    navigate(action!.href);
  };

  return (
    <Snackbar
      {...rest}
      open={true}
      onClose={onClose}
      TransitionComponent={Slide}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert
        severity={variant}
        action={
          <Stack direction="row" alignItems="center" spacing={1}>
            {action && (
              <Button
                component="a"
                href={action.href}
                onClick={handleActionClick}
                size="small"
                color="inherit"
                sx={{ textTransform: 'none' }}
              >
                {action.label}
              </Button>
            )}
            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        }
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {loading && <CircularProgress size={16} />}
          <Typography>{message}</Typography>
        </Stack>
      </Alert>
    </Snackbar>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);

  return {
    showNotification: (message: string, options?: Options) =>
      context.showNotification(message, options),
  };
}
